import FormTextArea from 'components/form/FormTextArea';
import { UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';
import { QuizEditFormStateType } from 'types/page/quizEditFormStateType';
import { AppHelper } from 'helpers/appHelper';
import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import { DivConst } from 'constants/divConst';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
/**
 * コンテンツ作成編集 取得項目設定コンポーネント
 */
export default function WsContentEditRespondentFields(props: {
  readOnly: boolean;
  contentCategory: { label: string; value: number };
  respondentFieldArray: UseFieldArrayReturn<
    QuizEditFormStateType | DiagnosisEditFormStateType,
    'respondentFields',
    'id'
  >;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const formContext = useFormContext();
  let typedFormContext;
  if (props.contentCategory.label === 'クイズ') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<QuizEditFormStateType>
    >;
  } else if (props.contentCategory.label === '診断') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<DiagnosisEditFormStateType>
    >;
  } else {
    throw new Error('無効なコンテンツカテゴリ');
  }
  const {
    register,
    setValue,
    formState: { errors },
    watch,
    clearErrors,
    setError,
  } = typedFormContext;

  const { move, fields } = props.respondentFieldArray;

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const [initialized, setInitialized] = useState(false);
  const respondentMail =
    {
      クイズ: AppHelper.getDivSelectOptions('quizRespondentMail'),
      診断: AppHelper.getDivSelectOptions('diagnosisRespondentMail'),
    }[props.contentCategory.label] || [];
  const reportMailDescribeHtmlPaceHolder =
    {
      クイズ:
        '（例）あなたの回答結果と本クイズの解説をクイズ完了後にメールでお送りいたします。※メールアドレス登録後にクイズが開始されます。',
      診断: '（例）診断結果と詳細なフィードバックを診断完了後にメールでもお送りします。',
    }[props.contentCategory.label] || '';

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        await _initState();
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
  }, []);

  // respondentMailDivとgetFlagの変更を監視
  const respondentMailDiv = watch('respondentMailDiv');
  const respondentFields = watch('respondentFields');

  useEffect(() => {
    // 項目取得なしの場合はエラーをクリア
    if (
      Number(respondentMailDiv) === DivConst.items.quizRespondentMail.none.value ||
      Number(respondentMailDiv) === DivConst.items.diagnosisRespondentMail.none.value
    ) {
      clearErrors('respondentMailDiv');
      return;
    }

    // 少なくとも1つの項目が取得対象になっているかチェック
    const hasGetFlag = respondentFields?.some(field => field.getFlag);
    if (!hasGetFlag) {
      setError('respondentMailDiv', {
        type: 'custom',
        message: '取得対象が選択されていません。少なくとも1つの項目を取得対象にしてください。',
      });
    } else {
      clearErrors('respondentMailDiv');
    }
  }, [respondentMailDiv, respondentFields]);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  const onMove = (fieldIndex: number, upper: boolean) => {
    // 先頭の場合は上に移動不可
    if (upper && fieldIndex === 0) {
      return;
    }
    // 最終行の場合は下に移動不可
    if (!upper && fieldIndex === fields.length - 1) {
      return;
    }
    // 要素の入れ替え
    move(fieldIndex, upper ? fieldIndex - 1 : fieldIndex + 1);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　画面の状態を初期化します。
   */
  const _initState = async () => {
    if (fields.length === 0) {
      setValue('respondentFields', _getDefaultFields());
    }
  };

  /**
   * 初期化時の取得項目を取得します。
   */
  const _getDefaultFields = () => {
    // 標準項目
    const respondentStandardFields = AppHelper.getDivSelectOptions(
      'respondentStandardFieldDiv'
    ).map((item, index) => ({
      no: index + 1,
      // メールアドレスはデフォルトで取得
      getFlag: Number(item.value) === DivConst.items.respondentStandardFieldDiv.mailAddress.value,
      respondentFieldDiv: DivConst.items.respondentFieldDiv.standard.value,
      respondentStandardFieldDiv: Number(item.value),
      label: '',
      placeholder: '',
      respondentCustomFieldDiv: 0,
      respondentCustomFieldInputDiv: 0,
      maxLength: 0,
    }));
    // 自由項目
    const respondentCustomFields = AppHelper.getDivSelectOptions('respondentCustomFieldDiv').map(
      (item, index) => ({
        no: respondentStandardFields.length + index + 1,
        getFlag: false,
        respondentFieldDiv: DivConst.items.respondentFieldDiv.custom.value,
        respondentStandardFieldDiv: 0,
        label: '',
        placeholder: '',
        respondentCustomFieldDiv: Number(item.value),
        respondentCustomFieldInputDiv: DivConst.items.respondentCustomFieldInputDiv.text.value,
        maxLength: DivConst.items.respondentCustomFieldMaxLength.maxLength20.value,
      })
    );
    return [...respondentStandardFields, ...respondentCustomFields];
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* 初期化中の表示 */}
      <IndicatorScreen visible={!initialized} />
      {/* 初期化後の表示 */}
      {initialized && (
        <section className="section-form-default">
          <div className="container">
            <div className="box-common shadow-none">
              <h3 className="title-section title-md">取得項目設定</h3>

              <div className="form-group">
                <FormSelect
                  subject="項目取得タイミング"
                  name="respondentMailDiv"
                  register={register}
                  errors={errors}
                  options={respondentMail}
                  disabled={props.readOnly}
                />
              </div>
              <div className="form-group">
                <FormTextArea
                  subject="メール取得時の説明文"
                  name="reportMailDescribeHtml"
                  placeholder={reportMailDescribeHtmlPaceHolder}
                  rows={5}
                  register={register}
                  errors={errors}
                  readOnly={props.readOnly}
                />
              </div>
              <div className="form-group">
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '120px' }}>順序</th>
                        <th style={{ width: '80px' }}>取得</th>
                        <th style={{ width: '100%' }}>項目</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((item, index) => (
                        <tr key={item.id}>
                          {/* 順序 */}
                          <td>
                            <div className="btn-group-header">
                              <div className="btn-group">
                                <button
                                  className="btn btn-sm"
                                  onClick={() => onMove(index, true)}
                                  disabled={props.readOnly}
                                >
                                  <img src="/img/icon/arrow-up-black.svg" alt="" />
                                </button>
                                <button
                                  className="btn btn-sm"
                                  onClick={() => onMove(index, false)}
                                  disabled={props.readOnly}
                                >
                                  <img
                                    src="/img/icon/arrow-up-black.svg"
                                    alt=""
                                    className="scaley-1"
                                  />
                                </button>
                              </div>
                            </div>
                          </td>
                          {/* 取得フラグ */}
                          <td className="text-center">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                disabled={props.readOnly}
                                {...register(`respondentFields.${index}.getFlag`)}
                              />
                            </div>
                          </td>
                          {/* 項目 */}
                          <td>
                            {/* 標準項目 */}
                            {item.respondentFieldDiv ===
                              DivConst.items.respondentFieldDiv.standard.value &&
                              AppHelper.getDivLabel(
                                'respondentStandardFieldDiv',
                                item.respondentStandardFieldDiv
                              )}
                            {/* 自由項目 */}
                            {item.respondentFieldDiv ===
                              DivConst.items.respondentFieldDiv.custom.value && (
                              <div className="d-flex flex-column gap-3">
                                <div className="d-flex gap-3">
                                  {/* 項目名 */}
                                  <div className="col-auto col-form-label">
                                    {AppHelper.getDivLabel(
                                      'respondentCustomFieldDiv',
                                      item.respondentCustomFieldDiv
                                    )}
                                  </div>
                                  {/* 質問文 */}
                                  <div className="flex-grow-1">
                                    <FormText
                                      name={`respondentFields.${index}.label`}
                                      required={watch(`respondentFields.${index}.getFlag`)}
                                      register={register}
                                      errors={errors}
                                      readOnly={props.readOnly}
                                      placeholder="質問文（例）あなたの〇〇を教えてください。"
                                      className="w-100"
                                    />
                                  </div>
                                  {/* プレースホルダー */}
                                  <div className="flex-grow-1">
                                    <FormText
                                      name={`respondentFields.${index}.placeholder`}
                                      register={register}
                                      errors={errors}
                                      readOnly={props.readOnly}
                                      placeholder="プレースホルダー"
                                      className="w-100"
                                    />
                                  </div>
                                </div>
                                <div className="d-flex align-items-center">
                                  {/* 入力タイプ */}
                                  <fieldset className="mb-0 p-0">
                                    <div className="form-check form-check-inline">
                                      <input
                                        type="radio"
                                        value={
                                          DivConst.items.respondentCustomFieldInputDiv.text.value
                                        }
                                        {...register(
                                          `respondentFields.${index}.respondentCustomFieldInputDiv`
                                        )}
                                        readOnly={props.readOnly}
                                        id={`textField-${index}`}
                                        className="form-check-input"
                                        defaultChecked={
                                          item.respondentCustomFieldInputDiv ===
                                          DivConst.items.respondentCustomFieldInputDiv.text.value
                                        }
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`textField-${index}`}
                                      >
                                        文字
                                      </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                      <input
                                        type="radio"
                                        value={
                                          DivConst.items.respondentCustomFieldInputDiv.numeric.value
                                        }
                                        {...register(
                                          `respondentFields.${index}.respondentCustomFieldInputDiv`
                                        )}
                                        readOnly={props.readOnly}
                                        id={`numberField-${index}`}
                                        className="form-check-input"
                                        defaultChecked={
                                          item.respondentCustomFieldInputDiv ===
                                          DivConst.items.respondentCustomFieldInputDiv.numeric.value
                                        }
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`numberField-${index}`}
                                      >
                                        数字
                                      </label>
                                    </div>
                                  </fieldset>
                                  {/* 文字数 */}
                                  <div className="d-flex align-items-center">
                                    <label className="form-label mb-0 me-2">文字数</label>
                                    <FormSelect
                                      name={`respondentFields.${index}.maxLength`}
                                      register={register}
                                      errors={errors}
                                      disabled={props.readOnly}
                                      options={AppHelper.getDivSelectOptions(
                                        'respondentCustomFieldMaxLength'
                                      )}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
